import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { APIResponseWrapper } from '../../models/common/api-response-wrapper';
import { SessionStorageService } from '../../services/session-storage.service';
import {
  HrBiometricAttendanceImport,
  HrBiometricConnection,
  HrBiometricProviderApp,
  HrBiometricSyncRun
} from './hr-biometric.models';

@Injectable({ providedIn: 'root' })
export class HrBiometricService {
  private readonly http = inject(HttpClient);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly baseUrl = `${environment.restApiUrl}hr/biometric`;

  getCompanyId(): number {
    return this.sessionStorageService.getCompany()?.id || this.sessionStorageService.getCustomer()?.company?.id || 0;
  }

  getCustomerId(): number {
    return this.sessionStorageService.getCustomer()?.id || 0;
  }

  marketplaceApps(): Observable<HrBiometricProviderApp[]> {
    return this.http.get<APIResponseWrapper<HrBiometricProviderApp[]>>(`${this.baseUrl}/marketplace/apps`)
      .pipe(map(response => response.data || []));
  }

  connections(companyId = this.getCompanyId()): Observable<HrBiometricConnection[]> {
    return this.http.get<APIResponseWrapper<HrBiometricConnection[]>>(`${this.baseUrl}/connections`, {
      params: new HttpParams().set('owningCompanyId', companyId)
    }).pipe(map(response => response.data || []));
  }

  saveConnection(connection: HrBiometricConnection): Observable<HrBiometricConnection> {
    const payload: HrBiometricConnection = {
      ...connection,
      owningCompanyId: connection.owningCompanyId || this.getCompanyId()
    };
    const request = payload.connectionId
      ? this.http.put<APIResponseWrapper<HrBiometricConnection>>(`${this.baseUrl}/connections/${payload.connectionId}`, payload)
      : this.http.post<APIResponseWrapper<HrBiometricConnection>>(`${this.baseUrl}/connections`, payload);
    return request.pipe(map(response => response.data as HrBiometricConnection));
  }

  testConnection(connectionId: number, companyId = this.getCompanyId()): Observable<HrBiometricSyncRun> {
    const params = new HttpParams()
      .set('owningCompanyId', companyId)
      .set('requestedByCustomerId', this.getCustomerId());
    return this.http.post<APIResponseWrapper<HrBiometricSyncRun>>(`${this.baseUrl}/connections/${connectionId}/test`, {}, { params })
      .pipe(map(response => response.data as HrBiometricSyncRun));
  }

  syncConnection(connectionId: number, companyId = this.getCompanyId()): Observable<HrBiometricSyncRun> {
    const params = new HttpParams()
      .set('owningCompanyId', companyId)
      .set('requestedByCustomerId', this.getCustomerId());
    return this.http.post<APIResponseWrapper<HrBiometricSyncRun>>(`${this.baseUrl}/connections/${connectionId}/sync`, {}, { params })
      .pipe(map(response => response.data as HrBiometricSyncRun));
  }

  runs(companyId = this.getCompanyId()): Observable<HrBiometricSyncRun[]> {
    return this.http.get<APIResponseWrapper<HrBiometricSyncRun[]>>(`${this.baseUrl}/runs`, {
      params: new HttpParams().set('owningCompanyId', companyId)
    }).pipe(map(response => response.data || []));
  }

  events(companyId = this.getCompanyId()): Observable<HrBiometricAttendanceImport[]> {
    return this.http.get<APIResponseWrapper<HrBiometricAttendanceImport[]>>(`${this.baseUrl}/events`, {
      params: new HttpParams().set('owningCompanyId', companyId)
    }).pipe(map(response => response.data || []));
  }
}
